import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Send, MessageSquare, Users, Clock } from 'lucide-react';

const AdminMessaging = () => {
  const { user, API } = useContext(AuthContext);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sentMessages, setSentMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSentMessages();
  }, []);

  const fetchSentMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages/broadcast`);
      setSentMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!title.trim() || !message.trim()) {
      toast.error('Please fill in both title and message');
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/admin/broadcast`, {
        title,
        message
      });
      
      toast.success('Message sent to all users!');
      setTitle('');
      setMessage('');
      fetchSentMessages();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to send message';
      toast.error(errorMsg);
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Check if current user is Jason Owens
  if (user?.email !== 'jason@westlandfmc.com') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <Card>
            <CardContent className="pt-6 text-center py-12">
              <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">
                Access Restricted
              </h3>
              <p className="text-slate-600">
                Only Jason Owens can access the broadcast messaging system.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="mb-8 animate-fade-in">
          <h1 className="text-4xl font-heading font-bold text-[#2B6B7F] mb-2">
            Broadcast Messaging
          </h1>
          <p className="text-slate-600">Send messages to all app users</p>
        </div>

        {/* Send Message Card */}
        <Card className="mb-8 border-t-4 border-t-[#2B6B7F]">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Send className="w-6 h-6 text-[#2B6B7F]" />
              <div>
                <CardTitle className="text-2xl font-heading text-[#2B6B7F]">
                  Send New Message
                </CardTitle>
                <CardDescription>
                  Broadcast a message to all users in the app
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block text-slate-700">
                Message Title
              </label>
              <Input
                placeholder="e.g., Special Announcement, Event Reminder, etc."
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="text-base"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block text-slate-700">
                Message Content
              </label>
              <Textarea
                placeholder="Type your message here. This will be sent to all users and will appear in their Messages section."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                className="resize-none text-base"
              />
              <p className="text-xs text-slate-500 mt-2">
                {message.length} characters
              </p>
            </div>

            <div className="flex items-center gap-3 pt-4">
              <Button
                onClick={handleSendMessage}
                disabled={sending || !title.trim() || !message.trim()}
                className="bg-[#2B6B7F] hover:bg-[#1E4D5F]"
                size="lg"
              >
                <Send className="w-4 h-4 mr-2" />
                {sending ? 'Sending...' : 'Send to All Users'}
              </Button>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Users className="w-4 h-4" />
                <span>Will notify all active users</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sent Messages History */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl font-heading text-[#2B6B7F]">
              Message History
            </CardTitle>
            <CardDescription>
              Previously sent broadcast messages
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center text-slate-600 py-8">Loading messages...</p>
            ) : sentMessages.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">No messages sent yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {sentMessages.map((msg) => (
                  <Card key={msg.id} className="border-l-4 border-l-[#5FA8C1]">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-slate-800 text-lg mb-1">
                            {msg.title}
                          </h4>
                          <div className="flex items-center gap-3 text-sm text-slate-500">
                            <div className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDate(msg.sent_at)}
                            </div>
                            <Badge variant="outline" className="text-xs">
                              {msg.read_by?.length || 0} reads
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                        {msg.message}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminMessaging;
